import React, { useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../components/mainLayout";
import Card from "../components/ui/card";
import { api } from "../api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../sass/pages/dashFinanceiro.scss";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
} from "recharts";

import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  PieChart as PieIcon,
  PhoneCall,
  Users,
  UserPlus,
  UserX,
  ShieldAlert,
  Wallet,
  Activity,
  Target,
  BriefcaseBusiness,
  Lock,
} from "lucide-react";

const BASE = "/ssw/financeiro/dash";

const BUCKET_ORDER = ["0-15", "16-30", "31-60", "61-90", "90+"];

const cores = [
  "#0a66c2",
  "#2e8b57",
  "#d32f2f",
  "#ff8f00",
  "#6a1b9a",
  "#0288d1",
];

/* ===================== UTILS ===================== */

const pad = (n) => String(n).padStart(2, "0");

const toInputDate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const todayISO = () => toInputDate(new Date());

const currentMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};

const firstDayMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
};

const parseLocalDate = (value) => {
  if (!value) return null;

  const [y, m, d] = String(value).split("-").map(Number);

  if (!y || !m || !d) return null;

  return new Date(y, m - 1, d, 0, 0, 0, 0);
};

const compareISODate = (a, b) => {
  const da = parseLocalDate(a);
  const db = parseLocalDate(b);

  if (!da || !db) return 0;

  return da.getTime() - db.getTime();
};

const diffDays = (from, to) => {
  const d1 = parseLocalDate(from);
  const d2 = parseLocalDate(to);

  if (!d1 || !d2) return 0;

  return Math.max(1, Math.round((d2 - d1) / 86400000) + 1);
};

const resolveGranularity = (from, to, manual) => {
  if (manual && manual !== "auto") return manual;

  const days = diffDays(from, to);

  if (days <= 35) return "day";
  if (days <= 120) return "week";

  return "month";
};

const fmtMoeda = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const fmtInt = (v) => (Number(v) || 0).toLocaleString("pt-BR");

const fmtDias = (v) => `${Math.round(Number(v) || 0)}d`;

const fmtPercent = (v) =>
  `${(Number(v) || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;

const maskDoc = (doc) => {
  const d = (doc || "").replace(/\D/g, "");

  if (d.length === 14) {
    return d.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/,
      "$1.$2.$3/$4-$5"
    );
  }

  if (d.length === 11) {
    return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, "$1.$2.$3-$4");
  }

  return doc || "-";
};

const normalizeFluxoReceb = (items = []) =>
  items.map((r) => ({
    bucket: String(r.bucket || ""),
    Previsto: Number(r.receberPrevisto || 0),
    Recebido: Number(r.recebidoReal || 0),
  }));

const normalizeFluxoPagar = (items = []) =>
  items.map((r) => ({
    bucket: String(r.bucket || ""),
    Previsto: Number(r.pagarPrevisto || 0),
    Pago: Number(r.pagoReal || 0),
  }));

const hasPositiveValue = (data = [], keys = []) =>
  data.some((row) => keys.some((key) => Number(row?.[key] || 0) > 0));

const hasAnyValue = (data = [], keys = []) =>
  data.some((row) => keys.some((key) => Number(row?.[key] || 0) !== 0));

const calcMaxValue = (data = [], keys = []) => {
  let max = 0;

  for (const row of data) {
    for (const key of keys) {
      const v = Math.abs(Number(row?.[key] || 0));
      if (v > max) max = v;
    }
  }

  return max;
};

const axisCurrencyTick = (v) => {
  const n = Number(v || 0);

  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(0)}k`;

  return `${n}`;
};

const axisNumberTick = (v) => {
  const n = Number(v || 0);

  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(0)}k`;

  return `${Math.round(n)}`;
};

const tendenciaClass = (valor) => {
  const n = Number(valor || 0);

  if (n > 0) return "positivo";
  if (n < 0) return "negativo";

  return "neutro";
};

const tendenciaTexto = (valor) => {
  const n = Number(valor || 0);

  if (n > 0) return `+${fmtPercent(n)}`;
  if (n < 0) return fmtPercent(n);

  return "0,00%";
};

const pickNumber = (row, keys = []) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== "") {
      return Number(row[key]) || 0;
    }
  }

  return 0;
};

const normalizeTrend = (
  items = [],
  {
    atualKeys = ["atual"],
    anteriorKeys = ["anterior"],
    cumulativeFallback = false,
  } = {}
) => {
  const rows = Array.isArray(items) ? items : [];

  const hasExplicitAtualAcumulado = rows.some((row) =>
    atualKeys.some(
      (key) =>
        String(key).toLowerCase().includes("acumulado") &&
        row?.[key] !== undefined &&
        row?.[key] !== null
    )
  );

  const hasExplicitAnteriorAcumulado = rows.some((row) =>
    anteriorKeys.some(
      (key) =>
        String(key).toLowerCase().includes("acumulado") &&
        row?.[key] !== undefined &&
        row?.[key] !== null
    )
  );

  let accAtual = 0;
  let accAnterior = 0;

  return rows.map((row, index) => {
    const valorAtual = pickNumber(row, atualKeys);
    const valorAnterior = pickNumber(row, anteriorKeys);

    if (cumulativeFallback && !hasExplicitAtualAcumulado) {
      accAtual += valorAtual;
    } else {
      accAtual = valorAtual;
    }

    if (cumulativeFallback && !hasExplicitAnteriorAcumulado) {
      accAnterior += valorAnterior;
    } else {
      accAnterior = valorAnterior;
    }

    const diaRaw =
      row?.dia ??
      row?.day ??
      row?.bucket ??
      row?.data ??
      row?.dt ??
      row?.periodo ??
      index + 1;

    const dia =
      Number.isFinite(Number(diaRaw)) && String(diaRaw).length <= 2
        ? pad(Number(diaRaw))
        : String(diaRaw);

    return {
      dia,
      Atual: accAtual,
      Anterior: accAnterior,
    };
  });
};

const mergeTrendSubtract = (a = [], b = []) => {
  const mapA = new Map((a || []).map((x) => [String(x.dia), x]));
  const mapB = new Map((b || []).map((x) => [String(x.dia), x]));

  const dias = Array.from(new Set([...mapA.keys(), ...mapB.keys()])).sort();

  return dias.map((dia) => ({
    dia,
    Atual: Number(mapA.get(dia)?.Atual || 0) - Number(mapB.get(dia)?.Atual || 0),
    Anterior:
      Number(mapA.get(dia)?.Anterior || 0) -
      Number(mapB.get(dia)?.Anterior || 0),
  }));
};

const EmptyChartState = ({ texto = "Sem dados no período." }) => (
  <div className="chart-empty-state">
    <span>{texto}</span>
  </div>
);

const MiniKpi = ({ titulo, valor, detalhe, tipo = "neutro", icon }) => (
  <div className={`mini-kpi-card ${tipo}`}>
    <div className="mini-kpi-icon">{icon}</div>
    <div className="mini-kpi-content">
      <span className="mini-kpi-title">{titulo}</span>
      <strong>{valor}</strong>
      {detalhe ? <small>{detalhe}</small> : null}
    </div>
  </div>
);

const VariacaoLinha = ({ titulo, data, formatter = fmtMoeda }) => {
  const percentual = Number(data?.percentual || 0);

  return (
    <div className="comparativo-linha">
      <div>
        <strong>{titulo}</strong>
        <span>
          Atual: {formatter(data?.atual)} • Anterior: {formatter(data?.anterior)}
        </span>
      </div>

      <div className={`comparativo-valor ${tendenciaClass(percentual)}`}>
        <strong>{formatter(data?.diferenca)}</strong>
        <small>{tendenciaTexto(percentual)}</small>
      </div>
    </div>
  );
};

const GraficoComparativoMensal = ({
  titulo,
  subtitulo,
  data = [],
  formatter = fmtMoeda,
  tickFormatter = axisCurrencyTick,
  emptyText = "Sem dados de tendência para o período.",
  height = 330,
  icon = null,
  colorAtual = "#3b82f6",
  colorAnterior = "#ec4899",
}) => {
  const hasData = hasAnyValue(data, ["Atual", "Anterior"]);

  const ultimo = Array.isArray(data) && data.length > 0 ? data[data.length - 1] : null;
  const atualFinal = Number(ultimo?.Atual || 0);
  const anteriorFinal = Number(ultimo?.Anterior || 0);
  const diff = atualFinal - anteriorFinal;
  const perc =
    anteriorFinal === 0 ? (atualFinal > 0 ? 100 : 0) : (diff / anteriorFinal) * 100;

  return (
    <div className="grafico grafico-large grafico-tendencia">
      <div className="grafico-header">
        <div>
          <h3 className="grafico-titulo">{titulo}</h3>
          <span className="grafico-sub">{subtitulo}</span>
        </div>

        {icon}
      </div>

      {!hasData ? (
        <EmptyChartState texto={emptyText} />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data} margin={{ top: 12, right: 22, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />

              <XAxis
                dataKey="dia"
                tick={{ fontSize: 12, fill: "#AAB6D3" }}
                minTickGap={18}
              />

              <YAxis
                tickFormatter={tickFormatter}
                tick={{ fontSize: 12, fill: "#AAB6D3" }}
                domain={["auto", "auto"]}
              />

              <Tooltip
                formatter={(value, name) => [formatter(value), name]}
                labelFormatter={(label) => `Dia ${label}`}
              />

              <Legend />

              <Line
                type="monotone"
                dataKey="Atual"
                name="Mês atual"
                stroke={colorAtual}
                strokeWidth={3}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
              />

              <Line
                type="monotone"
                dataKey="Anterior"
                name="Mês anterior"
                stroke={colorAnterior}
                strokeWidth={3}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>

          <div className="comparativo-lista">
            <div className="comparativo-linha">
              <div>
                <strong>Fechamento comparativo</strong>
                <span>
                  Atual: {formatter(atualFinal)} • Anterior: {formatter(anteriorFinal)}
                </span>
              </div>

              <div className={`comparativo-valor ${tendenciaClass(perc)}`}>
                <strong>{formatter(diff)}</strong>
                <small>{tendenciaTexto(perc)}</small>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ===================== COMPONENT ===================== */

const FinanceDash = () => {
  const [filtros, setFiltros] = useState({
    mes: currentMonthISO(),
    from: firstDayMonthISO(),
    to: todayISO(),
    granularity: "auto",
  });

  const [loading, setLoading] = useState(true);
  const [executivo, setExecutivo] = useState(null);
  const [kpis, setKpis] = useState({});
  const [fluxo, setFluxo] = useState({ recebiveis: [], pagaveis: [] });
  const [agingRec, setAgingRec] = useState([]);
  const [agingPag, setAgingPag] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [devedores, setDevedores] = useState([]);
  const [perf, setPerf] = useState({});
  const [erro, setErro] = useState("");
const requestIdRef = useRef(0);


  const loadAll = async (filtrosAtuais = filtros) => {
  const requestId = ++requestIdRef.current;

  setLoading(true);
  setErro("");

  try {
    const { from, to, mes } = filtrosAtuais;

    const granularity = resolveGranularity(
      from,
      to,
      filtrosAtuais.granularity
    );

    const calls = [
      api.get(`${BASE}/executivo`, { params: { mes } }),
      api.get(`${BASE}/kpis`, { params: { from, to } }),
      api.get(`${BASE}/fluxo`, { params: { from, to, granularity } }),
      api.get(`${BASE}/recebiveis/aging`, { params: { asOf: to } }),
      api.get(`${BASE}/pagaveis/aging`, { params: { asOf: to } }),
      api.get(`${BASE}/pagaveis/categorias`, {
        params: { from, to, mode: "caixa" },
      }),
      api.get(`${BASE}/recebiveis/top-devedores`, {
        params: { limit: 10 },
      }),
      api.get(`${BASE}/cobranca/performance`, { params: { from, to } }),
    ];

    const [
      execRes,
      kpisRes,
      fluxoRes,
      agingRecRes,
      agingPagRes,
      catRes,
      devRes,
      perfRes,
    ] = await Promise.allSettled(calls);

    // Impede resposta antiga de sobrescrever resposta nova
    if (requestId !== requestIdRef.current) return;

    if (execRes.status === "fulfilled" && execRes.value?.data) {
      setExecutivo(execRes.value.data);
    } else {
      setErro("Resumo executivo não carregado. Validar endpoint /executivo.");
    }

    if (kpisRes.status === "fulfilled") {
      setKpis(kpisRes.value.data || {});
    }

    if (fluxoRes.status === "fulfilled") {
      setFluxo(fluxoRes.value.data || { recebiveis: [], pagaveis: [] });
    }

    if (agingRecRes.status === "fulfilled") {
      setAgingRec(agingRecRes.value.data || []);
    }

    if (agingPagRes.status === "fulfilled") {
      setAgingPag(agingPagRes.value.data || []);
    }

    if (catRes.status === "fulfilled") {
      setCategorias(catRes.value.data || []);
    }

    if (devRes.status === "fulfilled") {
      setDevedores(devRes.value.data || []);
    }

    if (perfRes.status === "fulfilled") {
      setPerf(perfRes.value.data || {});
    }

    const hasFailure = [
      execRes,
      kpisRes,
      fluxoRes,
      agingRecRes,
      agingPagRes,
      catRes,
      devRes,
      perfRes,
    ].some((x) => x.status === "rejected");

    if (hasFailure) {
      setErro("Parte dos dados não foi carregada. Validar endpoints com falha.");
    }
  } catch (err) {
    if (requestId !== requestIdRef.current) return;

    setErro("Falha ao carregar dashboard financeiro.");
    console.error("[FinanceDash] Erro ao carregar dashboard:", err);
  } finally {
    if (requestId === requestIdRef.current) {
      setLoading(false);
    }
  }
};

  useEffect(() => {
    loadAll(filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAplicarFiltros = () => {
    const next = { ...filtros };

    if (compareISODate(next.from, next.to) > 0) {
      next.to = next.from;
      setFiltros(next);
    }

    loadAll(next);
  };

  const granularityEfetiva = useMemo(
    () => resolveGranularity(filtros.from, filtros.to, filtros.granularity),
    [filtros]
  );

  const fluxoReceb = useMemo(
    () => normalizeFluxoReceb(fluxo?.recebiveis || []),
    [fluxo]
  );

  const fluxoPagar = useMemo(
    () => normalizeFluxoPagar(fluxo?.pagaveis || []),
    [fluxo]
  );

  const agingRecData = useMemo(() => {
    const map = new Map(
      (agingRec || []).map((x) => [String(x.bucket), Number(x.total || 0)])
    );

    return BUCKET_ORDER.map((b) => ({ faixa: b, total: map.get(b) || 0 }));
  }, [agingRec]);

  const agingPagData = useMemo(() => {
    const map = new Map(
      (agingPag || []).map((x) => [String(x.bucket), Number(x.total || 0)])
    );

    return BUCKET_ORDER.map((b) => ({ faixa: b, total: map.get(b) || 0 }));
  }, [agingPag]);

  const diagnostico = executivo?.diagnostico || {};
  const dre = executivo?.dre || {};
  const clientes = executivo?.clientes || {};
  const inadimplencia = executivo?.inadimplencia || {};
  const comparativo = executivo?.comparativo || {};
  const acoes = executivo?.acoes || [];
  const tendencias = executivo?.tendencias || {};
  const categoriasExecutivo = executivo?.categoriasDespesas || [];
  const topDevedoresExecutivo = executivo?.topDevedores || [];

  const categoriasBase =
    categorias && categorias.length > 0 ? categorias : categoriasExecutivo;

  const categoriasData = useMemo(() => {
    return [...(categoriasBase || [])]
      .map((c) => ({
        categoria: c.categoria || "-",
        Pago: Number(c.valorPago || c.valorPrevisto || 0),
      }))
      .filter((x) => x.Pago > 0)
      .sort((a, b) => b.Pago - a.Pago)
      .slice(0, 12);
  }, [categoriasBase]);

  const devedoresRender =
    topDevedoresExecutivo.length > 0 ? topDevedoresExecutivo : devedores;

  const fluxoRecebHasData = useMemo(
    () => hasPositiveValue(fluxoReceb, ["Previsto", "Recebido"]),
    [fluxoReceb]
  );

  const fluxoPagarHasData = useMemo(
    () => hasPositiveValue(fluxoPagar, ["Previsto", "Pago"]),
    [fluxoPagar]
  );

  const agingRecHasData = useMemo(
    () => hasPositiveValue(agingRecData, ["total"]),
    [agingRecData]
  );

  const agingPagHasData = useMemo(
    () => hasPositiveValue(agingPagData, ["total"]),
    [agingPagData]
  );

  const fluxoRecebSingleBucket = fluxoReceb.length <= 1;
  const fluxoPagarSingleBucket = fluxoPagar.length <= 1;

  const fluxoRecebMax = calcMaxValue(fluxoReceb, ["Previsto", "Recebido"]);
  const fluxoPagarMax = calcMaxValue(fluxoPagar, ["Previsto", "Pago"]);
  const agingRecMax = calcMaxValue(agingRecData, ["total"]);
  const agingPagMax = calcMaxValue(agingPagData, ["total"]);
  const categoriasMax = calcMaxValue(categoriasData, ["Pago"]);

  const receitaGeradaTrend = useMemo(
    () =>
      normalizeTrend(tendencias?.receitaGerada || [], {
        atualKeys: ["atualAcumulado", "atual", "receitaAtual", "receitaGeradaAtual"],
        anteriorKeys: [
          "anteriorAcumulado",
          "anterior",
          "receitaAnterior",
          "receitaGeradaAnterior",
        ],
        cumulativeFallback: true,
      }),
    [tendencias]
  );

  const receitaRecebidaTrend = useMemo(
    () =>
      normalizeTrend(tendencias?.receitaRecebida || [], {
        atualKeys: [
          "atualAcumulado",
          "atual",
          "recebidoAtual",
          "receitaRecebidaAtual",
        ],
        anteriorKeys: [
          "anteriorAcumulado",
          "anterior",
          "recebidoAnterior",
          "receitaRecebidaAnterior",
        ],
        cumulativeFallback: true,
      }),
    [tendencias]
  );

  const despesasTrend = useMemo(
    () =>
      normalizeTrend(tendencias?.despesas || [], {
        atualKeys: [
          "atualAcumulado",
          "atual",
          "despesaAtual",
          "despesasAtual",
          "despesasCompetenciaAtual",
        ],
        anteriorKeys: [
          "anteriorAcumulado",
          "anterior",
          "despesaAnterior",
          "despesasAnterior",
          "despesasCompetenciaAnterior",
        ],
        cumulativeFallback: true,
      }),
    [tendencias]
  );

  const resultadoOperacionalTrendDireto = useMemo(
    () =>
      normalizeTrend(tendencias?.resultadoOperacional || [], {
        atualKeys: [
          "atualAcumulado",
          "atual",
          "resultadoAtual",
          "resultadoOperacionalAtual",
        ],
        anteriorKeys: [
          "anteriorAcumulado",
          "anterior",
          "resultadoAnterior",
          "resultadoOperacionalAnterior",
        ],
        cumulativeFallback: true,
      }),
    [tendencias]
  );

  const resultadoOperacionalTrend = useMemo(() => {
    if (hasAnyValue(resultadoOperacionalTrendDireto, ["Atual", "Anterior"])) {
      return resultadoOperacionalTrendDireto;
    }

    if (
      hasAnyValue(receitaGeradaTrend, ["Atual", "Anterior"]) ||
      hasAnyValue(despesasTrend, ["Atual", "Anterior"])
    ) {
      return mergeTrendSubtract(receitaGeradaTrend, despesasTrend);
    }

    return [];
  }, [resultadoOperacionalTrendDireto, receitaGeradaTrend, despesasTrend]);

  const clientesSaldoTrend = useMemo(
    () =>
      normalizeTrend(tendencias?.clientes || tendencias?.saldoClientes || [], {
        atualKeys: [
          "saldoAtualAcumulado",
          "saldoAtual",
          "saldoLiquidoAtual",
          "atual",
        ],
        anteriorKeys: [
          "saldoAnteriorAcumulado",
          "saldoAnterior",
          "saldoLiquidoAnterior",
          "anterior",
        ],
        cumulativeFallback: true,
      }),
    [tendencias]
  );

  const novosClientesTrend = useMemo(
    () =>
      normalizeTrend(tendencias?.clientes || tendencias?.novosClientes || [], {
        atualKeys: ["novosAtualAcumulado", "novosAtual", "atual"],
        anteriorKeys: ["novosAnteriorAcumulado", "novosAnterior", "anterior"],
        cumulativeFallback: true,
      }),
    [tendencias]
  );

  const cancelamentosTrend = useMemo(
    () =>
      normalizeTrend(tendencias?.clientes || tendencias?.cancelamentos || [], {
        atualKeys: ["canceladosAtualAcumulado", "canceladosAtual", "atual"],
        anteriorKeys: [
          "canceladosAnteriorAcumulado",
          "canceladosAnterior",
          "anterior",
        ],
        cumulativeFallback: true,
      }),
    [tendencias]
  );

  const inadimplenciaTrend = useMemo(
    () =>
      normalizeTrend(tendencias?.inadimplencia || [], {
        atualKeys: [
          "atualAcumulado",
          "atual",
          "vencidoAtual",
          "inadimplenciaAtual",
        ],
        anteriorKeys: [
          "anteriorAcumulado",
          "anterior",
          "vencidoAnterior",
          "inadimplenciaAnterior",
        ],
        cumulativeFallback: false,
      }),
    [tendencias]
  );

  if (loading) {
    return (
      <MainLayout>
        <div className="dashboard-container ssw-finance-dash">
          <div className="dashboard-header-skeleton">
            <Skeleton height={32} width={280} style={{ marginBottom: 10 }} />
            <Skeleton height={24} width={220} />
          </div>
          <Skeleton height={90} count={1} style={{ marginBottom: 20 }} />
          <Skeleton height={210} count={4} style={{ marginBottom: 20 }} />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="dashboard-container ssw-finance-dash">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Dashboard Financeiro</h1>
            <p className="dashboard-subtitle">
              DRE gerencial, liquidez, aging, tendências e cobrança •{" "}
              {filtros.from} a {filtros.to}
            </p>
          </div>

          <div className="dashboard-badge">
            <span className="badge-dot" />
            <span>Dados consolidados</span>
          </div>
        </div>

        {erro ? <div className="dashboard-alerta">{erro}</div> : null}

        <div className="dashboard-filtros">
          <div className="filtro-item">
            <label>Mês executivo / DRE</label>
            <input
              type="month"
              value={filtros.mes}
              onChange={(e) =>
                setFiltros((p) => ({
                  ...p,
                  mes: e.target.value,
                }))
              }
            />
          </div>

          <div className="filtro-item filtro-data-range">
            <label>De</label>
            <input
              type="date"
              value={filtros.from}
              onChange={(e) =>
                setFiltros((p) => ({
                  ...p,
                  from: e.target.value,
                }))
              }
            />

            <label>Até</label>
            <input
              type="date"
              value={filtros.to}
              max={todayISO()}
              onChange={(e) =>
                setFiltros((p) => ({
                  ...p,
                  to: e.target.value,
                }))
              }
            />
          </div>

          <div className="filtro-item">
            <label>Granularidade</label>
            <select
              value={filtros.granularity}
              onChange={(e) =>
                setFiltros((p) => ({
                  ...p,
                  granularity: e.target.value,
                }))
              }
            >
              <option value="auto">Automática</option>
              <option value="day">Dia</option>
              <option value="week">Semana</option>
              <option value="month">Mês</option>
            </select>
          </div>

          <div className="filtro-item filtro-actions">
            <button className="btn-ghost" onClick={onAplicarFiltros}>
              Filtrar
            </button>
          </div>
        </div>

        <section className={`executivo-panel ${diagnostico.status || "neutro"}`}>
          <div className="executivo-header">
            <div>
              <span className="executivo-label">Resumo Executivo</span>
              <h2>{diagnostico.mensagem || "Diagnóstico financeiro do mês"}</h2>
              <p>
                Competência {executivo?.periodo?.mesAtual || filtros.mes} vs{" "}
                {executivo?.periodo?.mesAnterior || "-"}
              </p>
            </div>

            <div className={`executivo-status ${diagnostico.status || "neutro"}`}>
              <Activity size={18} />
              <span>{diagnostico.status || "neutro"}</span>
            </div>
          </div>

          {Array.isArray(diagnostico.alertas) && diagnostico.alertas.length > 0 ? (
            <div className="executivo-alertas">
              {diagnostico.alertas.slice(0, 4).map((a, idx) => (
                <div key={`${a}-${idx}`} className="executivo-alerta-item">
                  <AlertTriangle size={16} />
                  <span>{a}</span>
                </div>
              ))}
            </div>
          ) : null}
        </section>

        <div className="indicadores-grid">
          <Card
            title="Receita Gerada"
            value={fmtMoeda(dre.receitaBruta)}
            icon={<TrendingUp size={22} color="#fff" />}
            color="#0a66c2"
            description={`Variação: ${tendenciaTexto(
              comparativo?.receitaGerada?.percentual
            )}`}
          />

          <Card
            title="Receita Recebida"
            value={fmtMoeda(dre.receitaRecebida)}
            icon={<Wallet size={22} color="#fff" />}
            color="#2e8b57"
            description={`Variação: ${tendenciaTexto(
              comparativo?.receitaRecebida?.percentual
            )}`}
          />

          <Card
            title="Resultado Operacional"
            value={fmtMoeda(dre.resultadoOperacional)}
            icon={<CheckCircle size={22} color="#fff" />}
            color={Number(dre.resultadoOperacional || 0) >= 0 ? "#2e8b57" : "#d32f2f"}
            description={`Margem: ${fmtPercent(dre.margemOperacional)}`}
          />

          <Card
            title="Inadimplência"
            value={fmtMoeda(inadimplencia.totalVencido)}
            icon={<AlertTriangle size={22} color="#fff" />}
            color="#ff8f00"
            description={`${fmtInt(
              inadimplencia.clientesInadimplentes
            )} cliente(s) inadimplente(s)`}
          />
        </div>

        <div className="indicadores-grid indicadores-secundarios">
          <Card
            title="Clientes Ativos"
            value={fmtInt(clientes.ativos)}
            icon={<Users size={22} color="#fff" />}
            color="#0a66c2"
            description={`BLQ=N • Ticket médio: ${fmtMoeda(clientes.ticketMedio)}`}
          />

          <Card
            title="Novos Clientes"
            value={fmtInt(clientes.novosMes)}
            icon={<UserPlus size={22} color="#fff" />}
            color="#2e8b57"
            description={`Mês anterior: ${fmtInt(clientes.novosMesAnterior)}`}
          />

          <Card
            title="Cancelamentos"
            value={fmtInt(clientes.canceladosMes)}
            icon={<UserX size={22} color="#fff" />}
            color="#d32f2f"
            description={`Perda estimada: ${fmtMoeda(
              clientes.receitaPerdidaCancelamento
            )}`}
          />

          <Card
            title="Clientes Bloqueados"
            value={fmtInt(clientes.bloqueados)}
            icon={<ShieldAlert size={22} color="#fff" />}
            color="#6a1b9a"
            description={`MRR bloqueado: ${fmtMoeda(clientes.mrrBloqueado)}`}
          />
        </div>

        <div className="graficos-grid">
          <GraficoComparativoMensal
            titulo="Tendência da Receita Gerada"
            subtitulo="Mês atual x mês anterior — acumulado diário"
            data={receitaGeradaTrend}
            formatter={fmtMoeda}
            tickFormatter={axisCurrencyTick}
            emptyText="Sem dados de receita gerada para tendência mensal."
            icon={<TrendingUp size={18} />}
            colorAtual="#3b82f6"
            colorAnterior="#ec4899"
          />

          <GraficoComparativoMensal
            titulo="Resultado Operacional"
            subtitulo="Receita gerada menos despesas — acumulado diário"
            data={resultadoOperacionalTrend}
            formatter={fmtMoeda}
            tickFormatter={axisCurrencyTick}
            emptyText="Sem dados de resultado operacional para tendência mensal."
            icon={<Target size={18} />}
            colorAtual="#22c55e"
            colorAnterior="#f59e0b"
          />
        </div>

        <div className="graficos-grid">
          <GraficoComparativoMensal
            titulo="Receita Recebida"
            subtitulo="Caixa realizado — mês atual x mês anterior"
            data={receitaRecebidaTrend}
            formatter={fmtMoeda}
            tickFormatter={axisCurrencyTick}
            emptyText="Sem dados de receita recebida para tendência mensal."
            icon={<Wallet size={18} />}
            colorAtual="#22c55e"
            colorAnterior="#60a5fa"
          />

          <GraficoComparativoMensal
            titulo="Despesas"
            subtitulo="Despesas por competência/pagamento — mês atual x mês anterior"
            data={despesasTrend}
            formatter={fmtMoeda}
            tickFormatter={axisCurrencyTick}
            emptyText="Sem dados de despesas para tendência mensal."
            icon={<TrendingDown size={18} />}
            colorAtual="#ef4444"
            colorAnterior="#f59e0b"
          />
        </div>

        <div className="graficos-grid">
          <GraficoComparativoMensal
            titulo="Saldo Líquido de Clientes"
            subtitulo="Novos clientes menos cancelamentos — mês atual x mês anterior"
            data={clientesSaldoTrend}
            formatter={fmtInt}
            tickFormatter={axisNumberTick}
            emptyText="Sem dados de entrada/cancelamento de clientes para tendência mensal."
            icon={<Users size={18} />}
            colorAtual="#06b6d4"
            colorAnterior="#a855f7"
          />

          <GraficoComparativoMensal
            titulo="Inadimplência"
            subtitulo="Saldo vencido em aberto — mês atual x mês anterior"
            data={inadimplenciaTrend}
            formatter={fmtMoeda}
            tickFormatter={axisCurrencyTick}
            emptyText="Sem dados de inadimplência para tendência mensal."
            icon={<AlertTriangle size={18} />}
            colorAtual="#f59e0b"
            colorAnterior="#ef4444"
          />
        </div>

        <div className="graficos-grid">
          <GraficoComparativoMensal
            titulo="Novos Clientes"
            subtitulo="Entradas acumuladas no mês — atual x anterior"
            data={novosClientesTrend}
            formatter={fmtInt}
            tickFormatter={axisNumberTick}
            emptyText="Sem dados de novos clientes para tendência mensal."
            icon={<UserPlus size={18} />}
            colorAtual="#22c55e"
            colorAnterior="#60a5fa"
          />

          <GraficoComparativoMensal
            titulo="Cancelamentos"
            subtitulo="Desistências acumuladas no mês — atual x anterior"
            data={cancelamentosTrend}
            formatter={fmtInt}
            tickFormatter={axisNumberTick}
            emptyText="Sem dados de cancelamentos para tendência mensal."
            icon={<UserX size={18} />}
            colorAtual="#ef4444"
            colorAnterior="#f59e0b"
          />
        </div>

        <div className="executivo-grid">
          <div className="grafico executivo-card">
            <div className="grafico-header">
              <div>
                <h3 className="grafico-titulo">DRE Gerencial</h3>
                <span className="grafico-sub">Competência selecionada</span>
              </div>
              <Target size={18} />
            </div>

            <div className="dre-lista">
              <div className="dre-linha positivo">
                <span>Receita Bruta</span>
                <strong>{fmtMoeda(dre.receitaBruta)}</strong>
              </div>

              <div className="dre-linha">
                <span>Receita Recebida</span>
                <strong>{fmtMoeda(dre.receitaRecebida)}</strong>
              </div>

              <div className="dre-linha negativo">
                <span>Despesas por Competência</span>
                <strong>{fmtMoeda(dre.despesasCompetencia)}</strong>
              </div>

              <div className="dre-linha negativo">
                <span>Despesas Pagas</span>
                <strong>{fmtMoeda(dre.despesasPagas)}</strong>
              </div>

              <div
                className={`dre-linha total ${
                  Number(dre.resultadoOperacional || 0) >= 0
                    ? "positivo"
                    : "negativo"
                }`}
              >
                <span>Resultado Operacional</span>
                <strong>{fmtMoeda(dre.resultadoOperacional)}</strong>
              </div>

              <div
                className={`dre-linha total ${
                  Number(dre.resultadoCaixa || 0) >= 0 ? "positivo" : "negativo"
                }`}
              >
                <span>Resultado de Caixa</span>
                <strong>{fmtMoeda(dre.resultadoCaixa)}</strong>
              </div>
            </div>
          </div>

          <div className="grafico executivo-card">
            <div className="grafico-header">
              <div>
                <h3 className="grafico-titulo">Comparativo Mensal</h3>
                <span className="grafico-sub">Mês atual x mês anterior</span>
              </div>
            </div>

            <div className="comparativo-lista">
              <VariacaoLinha titulo="Receita Gerada" data={comparativo.receitaGerada} />
              <VariacaoLinha titulo="Receita Recebida" data={comparativo.receitaRecebida} />
              <VariacaoLinha titulo="Despesas" data={comparativo.despesasCompetencia} />
              <VariacaoLinha
                titulo="Resultado Operacional"
                data={comparativo.resultadoOperacional}
              />
              <VariacaoLinha
                titulo="Novos Clientes"
                data={comparativo.novosClientes}
                formatter={fmtInt}
              />
              <VariacaoLinha
                titulo="Cancelamentos"
                data={comparativo.cancelamentos}
                formatter={fmtInt}
              />
            </div>
          </div>
        </div>

        <div className="executivo-grid">
          <div className="grafico executivo-card">
            <div className="grafico-header">
              <div>
                <h3 className="grafico-titulo">Pontos de Ação</h3>
                <span className="grafico-sub">
                  Priorização operacional para gestão
                </span>
              </div>
              <AlertTriangle size={18} />
            </div>

            <div className="acoes-lista">
              {acoes.length === 0 ? (
                <p className="lista-empty">Nenhum ponto crítico identificado.</p>
              ) : (
                acoes.map((acao, idx) => (
                  <div
                    key={`${acao.area || "acao"}-${idx}`}
                    className={`acao-item prioridade-${acao.prioridade || "media"}`}
                  >
                    <div className="acao-topo">
                      <strong>{acao.titulo}</strong>
                      <span>{acao.prioridade || "média"}</span>
                    </div>

                    <p>{acao.descricao}</p>

                    {acao.valor !== undefined ? (
                      <small>Impacto: {fmtMoeda(acao.valor)}</small>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="grafico executivo-card">
            <div className="grafico-header">
              <div>
                <h3 className="grafico-titulo">Carteira e Risco</h3>
                <span className="grafico-sub">
                  Base ativa, carteira total, bloqueios e inadimplência
                </span>
              </div>
              <ShieldAlert size={18} />
            </div>

            <div className="mini-kpi-grid">
              <MiniKpi
                titulo="MRR Ativo"
                valor={fmtMoeda(clientes.mrrAtual)}
                detalhe={`${fmtInt(clientes.ativos)} cliente(s) BLQ=N`}
                tipo="positivo"
                icon={<Wallet size={18} />}
              />

              <MiniKpi
                titulo="MRR Carteira"
                valor={fmtMoeda(clientes.mrrCarteira)}
                detalhe={`${fmtInt(clientes.carteiraTotal)} cliente(s) na base`}
                tipo="neutro"
                icon={<BriefcaseBusiness size={18} />}
              />

              <MiniKpi
                titulo="MRR Bloqueado"
                valor={fmtMoeda(clientes.mrrBloqueado)}
                detalhe={`${fmtInt(clientes.bloqueados)} cliente(s) BLQ=B`}
                tipo="alerta"
                icon={<Lock size={18} />}
              />

              <MiniKpi
                titulo="Saldo Líquido"
                valor={fmtInt(clientes.saldoLiquido)}
                detalhe="Novos - cancelados"
                tipo={Number(clientes.saldoLiquido || 0) >= 0 ? "positivo" : "negativo"}
                icon={<Users size={18} />}
              />

              <MiniKpi
                titulo="Liberação Especial"
                valor={fmtInt(clientes.liberacaoEspecial)}
                detalhe="Clientes BLQ=L"
                tipo="alerta"
                icon={<ShieldAlert size={18} />}
              />

              <MiniKpi
                titulo="Vencido Bloqueado"
                valor={fmtMoeda(inadimplencia.bloqueadosValor)}
                detalhe={`${fmtInt(inadimplencia.bloqueadosQtd)} cliente(s)`}
                tipo="negativo"
                icon={<AlertTriangle size={18} />}
              />
            </div>
          </div>
        </div>

        <div className="indicadores-grid">
          <Card
            title="Recebido no Período"
            value={fmtMoeda(kpis.recebidoPeriodo)}
            icon={<TrendingUp size={22} color="#fff" />}
            color="#2e8b57"
            description="Baixas de recebimento realizadas no intervalo selecionado."
          />

          <Card
            title="Pago no Período"
            value={fmtMoeda(kpis.pagoPeriodo)}
            icon={<TrendingDown size={22} color="#fff" />}
            color="#d32f2f"
            description="Baixas de pagamento no intervalo selecionado."
          />

          <Card
            title="Em Atraso (Recebíveis)"
            value={fmtMoeda(kpis.receberEmAtraso)}
            icon={<AlertTriangle size={22} color="#fff" />}
            color="#ff8f00"
            description={`Bloqueados: ${fmtMoeda(
              kpis.receberEmAtrasoBloqueados
            )}`}
          />

          <Card
            title="Em Atraso (Pagáveis)"
            value={fmtMoeda(kpis.pagarEmAtraso)}
            icon={<AlertTriangle size={22} color="#fff" />}
            color="#607d8b"
            description="Saldo vencido e não liquidado da carteira a pagar."
          />
        </div>

        <div className="indicadores-grid indicadores-secundarios">
          <Card
            title="DSO Estimado"
            value={fmtDias(kpis.dsoEstimado)}
            icon={<Clock size={22} color="#fff" />}
            color="#0288d1"
            description="Prazo médio estimado de conversão em caixa."
          />

          <Card
            title="DPO"
            value={fmtDias(kpis.dpo)}
            icon={<Clock size={22} color="#fff" />}
            color="#7b1fa2"
            description="Prazo médio de pagamento."
          />

          <Card
            title="Caixa Líquido (Período)"
            value={fmtMoeda(kpis.caixaLiquido)}
            icon={<CheckCircle size={22} color="#fff" />}
            color={Number(kpis.caixaLiquido || 0) >= 0 ? "#2e8b57" : "#d32f2f"}
            description="Recebido menos pago no período."
          />
        </div>

        <div className="graficos-grid">
          <div className="grafico grafico-large">
            <div className="grafico-header">
              <div>
                <h3 className="grafico-titulo">Fluxo de Recebíveis</h3>
                <span className="grafico-sub">
                  Previsto x Real por {granularityEfetiva}
                </span>
              </div>
            </div>

            {!fluxoRecebHasData ? (
              <EmptyChartState texto="Sem recebimentos ou previsões no período." />
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                {fluxoRecebSingleBucket ? (
                  <BarChart
                    data={fluxoReceb}
                    margin={{ top: 16, right: 20, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.08)"
                    />
                    <XAxis
                      dataKey="bucket"
                      tick={{ fontSize: 12, fill: "#AAB6D3" }}
                    />
                    <YAxis
                      tickFormatter={axisCurrencyTick}
                      tick={{ fontSize: 12, fill: "#AAB6D3" }}
                      domain={[0, Math.max(1, Math.ceil(fluxoRecebMax * 1.15))]}
                    />
                    <Tooltip formatter={(v) => fmtMoeda(v)} />
                    <Legend />
                    <Bar dataKey="Previsto" fill="#0a66c2" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="Recebido" fill="#2e8b57" radius={[6, 6, 0, 0]} />
                  </BarChart>
                ) : (
                  <LineChart
                    data={fluxoReceb}
                    margin={{ top: 16, right: 20, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.08)"
                    />
                    <XAxis
                      dataKey="bucket"
                      tick={{ fontSize: 12, fill: "#AAB6D3" }}
                      minTickGap={24}
                    />
                    <YAxis
                      tickFormatter={axisCurrencyTick}
                      tick={{ fontSize: 12, fill: "#AAB6D3" }}
                      domain={[0, Math.max(1, Math.ceil(fluxoRecebMax * 1.15))]}
                    />
                    <Tooltip formatter={(v) => fmtMoeda(v)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="Previsto"
                      stroke="#0a66c2"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="Recebido"
                      stroke="#2e8b57"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            )}
          </div>

          <div className="grafico grafico-large">
            <div className="grafico-header">
              <div>
                <h3 className="grafico-titulo">Fluxo de Pagáveis</h3>
                <span className="grafico-sub">
                  Previsto x Real por {granularityEfetiva}
                </span>
              </div>
            </div>

            {!fluxoPagarHasData ? (
              <EmptyChartState texto="Sem pagamentos ou previsões no período." />
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                {fluxoPagarSingleBucket ? (
                  <BarChart
                    data={fluxoPagar}
                    margin={{ top: 16, right: 20, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.08)"
                    />
                    <XAxis
                      dataKey="bucket"
                      tick={{ fontSize: 12, fill: "#AAB6D3" }}
                    />
                    <YAxis
                      tickFormatter={axisCurrencyTick}
                      tick={{ fontSize: 12, fill: "#AAB6D3" }}
                      domain={[0, Math.max(1, Math.ceil(fluxoPagarMax * 1.15))]}
                    />
                    <Tooltip formatter={(v) => fmtMoeda(v)} />
                    <Legend />
                    <Bar dataKey="Previsto" fill="#ff8f00" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="Pago" fill="#d32f2f" radius={[6, 6, 0, 0]} />
                  </BarChart>
                ) : (
                  <LineChart
                    data={fluxoPagar}
                    margin={{ top: 16, right: 20, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.08)"
                    />
                    <XAxis
                      dataKey="bucket"
                      tick={{ fontSize: 12, fill: "#AAB6D3" }}
                      minTickGap={24}
                    />
                    <YAxis
                      tickFormatter={axisCurrencyTick}
                      tick={{ fontSize: 12, fill: "#AAB6D3" }}
                      domain={[0, Math.max(1, Math.ceil(fluxoPagarMax * 1.15))]}
                    />
                    <Tooltip formatter={(v) => fmtMoeda(v)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="Previsto"
                      stroke="#ff8f00"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="Pago"
                      stroke="#d32f2f"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="graficos-grid">
          <div className="grafico">
            <div className="grafico-header">
              <div>
                <h3 className="grafico-titulo">Aging – Recebíveis</h3>
                <span className="grafico-sub">Carteira vencida por faixa</span>
              </div>
              <PieIcon size={18} />
            </div>

            {!agingRecHasData ? (
              <EmptyChartState texto="Sem títulos vencidos a receber." />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={agingRecData}
                  margin={{ top: 16, right: 20, left: 10, bottom: 10 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.08)"
                  />
                  <XAxis
                    dataKey="faixa"
                    tick={{ fontSize: 12, fill: "#AAB6D3" }}
                  />
                  <YAxis
                    tickFormatter={axisCurrencyTick}
                    tick={{ fontSize: 12, fill: "#AAB6D3" }}
                    domain={[0, Math.max(1, Math.ceil(agingRecMax * 1.15))]}
                  />
                  <Tooltip formatter={(v) => fmtMoeda(v)} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                    {agingRecData.map((_, idx) => (
                      <Cell key={idx} fill={cores[idx % cores.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="grafico">
            <div className="grafico-header">
              <div>
                <h3 className="grafico-titulo">Aging – Pagáveis</h3>
                <span className="grafico-sub">Carteira vencida por faixa</span>
              </div>
              <PieIcon size={18} />
            </div>

            {!agingPagHasData ? (
              <EmptyChartState texto="Sem títulos vencidos a pagar." />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={agingPagData}
                  margin={{ top: 16, right: 20, left: 10, bottom: 10 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.08)"
                  />
                  <XAxis
                    dataKey="faixa"
                    tick={{ fontSize: 12, fill: "#AAB6D3" }}
                  />
                  <YAxis
                    tickFormatter={axisCurrencyTick}
                    tick={{ fontSize: 12, fill: "#AAB6D3" }}
                    domain={[0, Math.max(1, Math.ceil(agingPagMax * 1.15))]}
                  />
                  <Tooltip formatter={(v) => fmtMoeda(v)} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                    {agingPagData.map((_, idx) => (
                      <Cell key={idx} fill={cores[(idx + 2) % cores.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="graficos-grid">
          <div className="grafico grafico-large">
            <div className="grafico-header">
              <div>
                <h3 className="grafico-titulo">Despesas por Categoria</h3>
                <span className="grafico-sub">
                  Maiores despesas do período / competência
                </span>
              </div>
            </div>

            {!hasPositiveValue(categoriasData, ["Pago"]) ? (
              <EmptyChartState texto="Sem pagamentos classificados no período." />
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart
                  layout="vertical"
                  data={categoriasData}
                  margin={{ top: 16, right: 30, left: 140, bottom: 10 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.08)"
                  />
                  <XAxis
                    type="number"
                    tickFormatter={axisCurrencyTick}
                    tick={{ fontSize: 12, fill: "#AAB6D3" }}
                    domain={[0, Math.max(1, Math.ceil(categoriasMax * 1.15))]}
                  />
                  <YAxis
                    dataKey="categoria"
                    type="category"
                    tick={{ fontSize: 12, fill: "#AAB6D3" }}
                    width={130}
                  />
                  <Tooltip formatter={(v) => fmtMoeda(v)} />
                  <Legend />
                  <Bar dataKey="Pago" fill="#2e8b57" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="grafico">
            <div className="grafico-header">
              <div>
                <h3 className="grafico-titulo">Top Devedores</h3>
                <span className="grafico-sub">Maiores saldos vencidos</span>
              </div>
            </div>

            <div className="lista-projetos-atrasados">
              {(!devedoresRender || devedoresRender.length === 0) && (
                <p className="lista-empty">Sem devedores em aberto.</p>
              )}

              {devedoresRender.map((d, idx) => (
                <div
                  key={`${d.codigoCad || d.cnpj || "sem-doc"}-${idx}`}
                  className="projeto-atrasado-item"
                >
                  <div className="projeto-atrasado-main">
                    <span className="projeto-titulo">{d.cliente || "-"}</span>
                    <span className="projeto-codigo">
                      {d.codigoCad ? `#${d.codigoCad} • ` : ""}
                      {maskDoc(d.cnpj)}
                    </span>

                    {d.statusCliente ? (
                      <span
                        className={
                          d.statusCliente === "BLOQUEADO"
                            ? "chip-bloqueado"
                            : "chip-ativo"
                        }
                      >
                        {d.statusCliente}
                      </span>
                    ) : null}
                  </div>

                  <div className="projeto-atrasado-meta">
                    <span className="chip-critico">{fmtMoeda(d.saldo)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="graficos-grid">
          <div className="grafico">
            <div className="grafico-header">
              <div>
                <h3 className="grafico-titulo">Funil de Cobrança</h3>
                <span className="grafico-sub">Contatado → Prometido → Pago</span>
              </div>
              <PhoneCall size={18} />
            </div>

            <div className="funil-grid">
              <Card
                title="Contatado"
                value={fmtInt(perf?.funil?.contatado || 0)}
                icon={<PhoneCall size={20} color="#fff" />}
                color="#0288d1"
                description="Contatos de cobrança registrados no período."
              />

              <Card
                title="Prometido"
                value={fmtInt(perf?.funil?.prometido || 0)}
                icon={<TrendingUp size={20} color="#fff" />}
                color="#6a1b9a"
                description="Promessas de pagamento registradas."
              />

              <Card
                title="Pago"
                value={fmtInt(perf?.funil?.pago || 0)}
                icon={<CheckCircle size={20} color="#fff" />}
                color="#2e8b57"
                description="Pagamentos efetivados após interação."
              />

              <Card
                title="TMA Promessa→Pagamento"
                value={fmtDias(perf?.tmaPromessaPagamentoDias || 0)}
                icon={<Clock size={20} color="#fff" />}
                color="#ff8f00"
                description="Tempo médio entre promessa e liquidação."
              />
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default FinanceDash;